import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRightIcon,
  BuildingOffice2Icon,
  CheckCircleIcon,
  CpuChipIcon,
  DocumentCheckIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  GlobeAltIcon,
  ServerStackIcon,
  ShieldCheckIcon,
  SignalIcon,
  TruckIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import {
  FaqAccordion,
  PageCta,
  PageHero,
  SectionHeading,
} from '../../components/page-templates/SitePageTemplates';

const heroPanelItems = [
  {
    icon: GlobeAltIcon,
    title: '150+ countries covered',
    description: 'Import ICT hardware into markets where you have no local entity through one accountable partner.',
  },
  {
    icon: ServerStackIcon,
    title: 'Specialist in ICT infrastructure',
    description: 'Purpose-built compliance model for servers, networking, storage, and telecom equipment.',
  },
  {
    icon: DocumentCheckIcon,
    title: 'End-to-end trade compliance',
    description: 'Customs declarations, HS classification, permits, and duty management handled in one flow.',
  },
];

const riskItems = [
  {
    icon: ExclamationTriangleIcon,
    title: 'Stuck shipments at customs',
    description: 'Missing documentation or misclassification delays clearance and disrupts rollout timing.',
  },
  {
    icon: XCircleIcon,
    title: 'Regulatory penalties',
    description: 'Controlled goods, dual-use items, and encryption require specific handling or exposure grows fast.',
  },
  {
    icon: ExclamationTriangleIcon,
    title: 'Project delays',
    description: 'Late arrivals cascade into installation, commissioning, and go-live slippage.',
  },
  {
    icon: XCircleIcon,
    title: 'Unexpected duties or tax exposure',
    description: 'Incorrect classification changes duty rates and tax treatment in ways that impact programme budgets.',
  },
];

const serviceCards = [
  {
    icon: DocumentTextIcon,
    title: 'Importer of Record (IOR)',
    description: 'Act as the legal importer for your shipments into markets where you have no local entity.',
    features: [
      'Act as the legal importer for your shipments',
      'Manage customs declarations and regulatory documentation',
      'Ensure correct HS classification',
      'Handle duties, taxes, and import permits',
      'Coordinate with local customs authorities',
    ],
  },
  {
    icon: GlobeAltIcon,
    title: 'Exporter of Record (EOR)',
    description: 'Assume legal responsibility for exports, reverse logistics, and regulatory export controls.',
    features: [
      'Manage export declarations',
      'Ensure compliance with export regulations',
      'Support reverse logistics and RMA exports',
      'Handle dual-use and encryption checks',
    ],
  },
  {
    icon: ShieldCheckIcon,
    title: 'Trade Compliance Support',
    description: 'Classification, licensing, and cross-border compliance coordination for ICT hardware movement.',
    features: [
      'Product classification',
      'Pre-shipment regulatory checks',
      'Import licence coordination',
      'Cross-border ICT asset movement',
    ],
  },
];

const advantages = [
  'Expertise in networking and telecom equipment',
  'We understand the deal structure between OEMs, distributors, system integrators and end customers',
  "Already serving many OEMs and distributors, so we may already be onboarded with your vendors",
  'Support for data centre expansions and global infrastructure rollouts',
  "We deliver fast quotes for complex multi-country RFQs",
];

const supportedHardware = [
  { icon: BuildingOffice2Icon, label: 'Data centre infrastructure' },
  { icon: ServerStackIcon, label: 'Servers and storage' },
  { icon: SignalIcon, label: 'Networking equipment' },
  { icon: TruckIcon, label: 'Telecom hardware' },
  { icon: CpuChipIcon, label: 'End-point devices and peripherals' },
  { icon: GlobeAltIcon, label: 'Branch office and remote-site equipment' },
];

const faqs = [
  {
    question: 'What is an Importer or Exporter of Record?',
    answer:
      'An Importer of Record (IOR) or Exporter of Record (EOR) is the legally responsible entity for a shipment when goods cross a border. This entity is responsible for: Customs declarations and regulatory documentation; Correct HS code classification; Payment of duties and taxes; Import or export permits and licences; Compliance with local trade laws and regulations. Without a compliant importer or exporter, shipments can be delayed, rejected, or penalised by customs authorities.',
  },
  {
    question: 'Do I need an Importer of Record to ship ICT equipment internationally?',
    answer: (
      <>
        No, only in cases where your customer is unable to act as the importer. This could be for several reasons:
        <ul className="mt-2 list-disc space-y-1 pl-5">
          <li>they are not legally present in the destination country</li>
          <li>they do not hold the relevant import licences</li>
          <li>the deal structure does not allow them to act as importer due to local currency control measures</li>
        </ul>
      </>
    ),
  },
  {
    question: 'Can TrueLog act as IOR for all kinds of cargo?',
    answer:
      'No. TrueLog focuses on networking equipment only, and cargo must be relevant to the IT industry. We do not move other types of cargo, such as biological or medical goods, which fall outside our area of expertise.',
  },
  {
    question: 'What is the difference between a freight forwarder and an IOR provider?',
    answer:
      'Freight forwarders move goods. An Importer of Record assumes the legal responsibility for regulatory compliance, duties, and customs documentation.',
  },
];

const ctaPills = [
  'Import ICT hardware into 150+ countries',
  'Specialists in networking and telecom',
  'End-to-end trade compliance and customs management',
];

const IORSolutions: React.FC = () => {
  return (
    <div className="bg-white">
      <PageHero
        eyebrow="Trade Compliance"
        title="Compliance as an Enabler: Global Importer & Exporter of Record for ICT Hardware"
        highlight="Global Importer & Exporter of Record for ICT Hardware"
        description="Ship servers, networking equipment, and ICT infrastructure into countries where you have no legal entity. TrueLog handles the compliance, customs responsibility, and regulatory requirements."
        heroIcon={DocumentTextIcon}
        accentIcon={ShieldCheckIcon}
        actions={[{ href: '/contact-us', label: 'Talk to a Specialist' }]}
        badges={[
          'Import ICT hardware into 150+ countries without a local entity',
          'Specialists in networking and telecom equipment',
          'End-to-end trade compliance and customs management',
        ]}
        metrics={[
          { value: '150+', label: 'Countries supported' },
          { value: '0', label: 'Local entities required' },
          { value: '1', label: 'Accountable partner' },
          { value: '24/7', label: 'Compliance oversight' },
        ]}
        panelEyebrow="What TrueLog owns"
        panelTitle="Compliance designed for ICT infrastructure"
        panelDescription="Purpose-built trade compliance for servers, networking, storage, and telecom hardware across regulated markets."
        panelItems={heroPanelItems}
      />

      <section className="bg-white py-20">
        <div className="container-custom max-w-7xl">
          <SectionHeading
            eyebrow="Why It Matters"
            title="Why ICT Hardware Requires Specialist Compliance"
            description="Technology equipment is subject to complex regulatory requirements. Many ICT products fall under dual-use regulations, encryption controls, or telecom compliance frameworks that vary significantly between countries."
          />

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mx-auto mb-10 max-w-3xl text-center text-sm font-semibold uppercase tracking-[0.16em] text-[#2447d8]"
          >
            Incorrect classification or missing documentation can lead to:
          </motion.p>

          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
            {riskItems.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: index * 0.06 }}
                className="rounded-[28px] border border-slate-200/80 bg-white p-6 shadow-[0_24px_60px_-40px_rgba(15,23,42,0.3)]"
              >
                <div className="inline-flex rounded-2xl bg-red-50 p-3 text-red-500">
                  <item.icon className="h-6 w-6" />
                </div>
                <h3 className="mt-5 text-lg font-semibold text-slate-950">{item.title}</h3>
                <p className="mt-3 text-sm leading-7 text-slate-600">{item.description}</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mx-auto mt-12 flex max-w-3xl flex-col items-center gap-5 text-center"
          >
            <p className="text-base leading-8 text-slate-600 sm:text-lg">
              TrueLog specialises in navigating these requirements for ICT and telecom infrastructure shipments.
            </p>
            <Link
              to="/contact-us"
              className="inline-flex items-center justify-center rounded-2xl bg-gradient-to-r from-[#0E9ED5] to-[#385CF2] px-7 py-4 text-sm font-bold uppercase tracking-[0.16em] text-white shadow-[0_20px_44px_-20px_rgba(14,158,213,0.95)] transition-all duration-300 hover:-translate-y-0.5"
            >
              Talk to a Specialist
              <ArrowRightIcon className="ml-2 h-5 w-5" />
            </Link>
          </motion.div>
        </div>
      </section>

      <section className="bg-slate-50 py-20">
        <div className="container-custom max-w-7xl">
          <SectionHeading
            eyebrow="Our Services"
            title="Our Global IOR & EOR Services"
            description="Three connected services that take responsibility for the legal, regulatory, and customs layer behind every cross-border ICT shipment."
          />

          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {serviceCards.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: index * 0.06 }}
                className="flex flex-col rounded-[30px] border border-slate-200/80 bg-white p-6 shadow-[0_24px_60px_-40px_rgba(15,23,42,0.3)]"
              >
                <div className="inline-flex w-fit rounded-2xl bg-gradient-to-br from-[#385CF2] to-[#0E9ED5] p-3 text-white">
                  <service.icon className="h-6 w-6" />
                </div>
                <h3 className="mt-5 text-2xl font-semibold text-slate-950">{service.title}</h3>
                <p className="mt-3 text-sm leading-7 text-slate-600">{service.description}</p>
                <ul className="mt-5 space-y-3">
                  {service.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-3 text-sm leading-7 text-slate-600">
                      <CheckCircleIcon className="mt-1 h-4 w-4 flex-shrink-0 text-[#0E9ED5]" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/contact-us"
                  className="mt-auto inline-flex w-fit items-center gap-2 pt-6 text-sm font-bold uppercase tracking-[0.14em] text-[#2447d8] hover:text-[#0E9ED5]"
                >
                  Get a Quote
                  <ArrowRightIcon className="h-4 w-4" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-20">
        <div className="container-custom max-w-7xl grid gap-12 lg:grid-cols-[0.95fr_1.05fr] lg:items-start">
          <motion.div
            initial={{ opacity: 0, x: -24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.75 }}
          >
            <span className="eyebrow-pill">Why TrueLog</span>
            <h2 className="mt-5 text-[30px] font-bold tracking-tight text-slate-950 sm:text-[38px]">
              Why ICT Companies Choose TrueLog
            </h2>
            <p className="mt-4 text-base leading-8 text-slate-600 sm:text-lg">
              TrueLog specialises in trade compliance for ICT infrastructure.
            </p>
            <p className="mt-3 text-sm font-semibold uppercase tracking-[0.16em] text-[#2447d8]">Key advantages:</p>
          </motion.div>

          <div className="grid gap-4">
            {advantages.map((item, index) => (
              <motion.div
                key={item}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.06 }}
                className="flex items-start gap-4 rounded-[24px] border border-slate-200/80 bg-white p-5 shadow-[0_20px_50px_-40px_rgba(15,23,42,0.25)]"
              >
                <div className="rounded-2xl bg-gradient-to-br from-[#385CF2] to-[#0E9ED5] p-2.5 text-white">
                  <CheckCircleIcon className="h-5 w-5" />
                </div>
                <p className="text-sm leading-7 text-slate-700">{item}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-20">
        <div className="container-custom max-w-7xl">
          <SectionHeading
            eyebrow="Integrated Model"
            title="Built for Global ICT Logistics"
            description="TrueLog integrates compliance with the broader logistics chain, ensuring that regulatory clearance, hardware movement, and final delivery operate as one coordinated system."
          />

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mx-auto mb-10 max-w-3xl text-center text-sm font-semibold uppercase tracking-[0.16em] text-[#2447d8]"
          >
            We support the global movement of:
          </motion.p>

          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {supportedHardware.map((item, index) => (
              <motion.div
                key={item.label}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.05 }}
                className="flex items-center gap-4 rounded-[24px] border border-slate-200/80 bg-white p-5 shadow-[0_20px_50px_-40px_rgba(15,23,42,0.25)]"
              >
                <div className="rounded-2xl bg-sky-50 p-3 text-sky-600">
                  <item.icon className="h-6 w-6" />
                </div>
                <p className="text-base font-semibold text-slate-950">{item.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-20">
        <div className="container-custom max-w-7xl">
          <SectionHeading
            eyebrow="FAQ"
            title="Common questions about trade compliance"
            description="The operational questions that come up most often when planning cross-border movement of ICT hardware."
          />
          <div className="mx-auto max-w-4xl">
            <FaqAccordion items={faqs} />
          </div>
        </div>
      </section>

      <PageCta
        title="Plan Your Global ICT Hardware Shipment"
        description="Whether you are shipping a single server or executing a multi-country rollout, TrueLog can provide the trade compliance framework required to move your equipment across borders."
        pills={ctaPills}
        primaryHref="/contact-us"
        primaryLabel="Contact Us"
        secondaryHref="/services"
        secondaryLabel="Explore services"
      />
    </div>
  );
};

export default IORSolutions;
